import React from 'react';
import { IconButton, MenuItem, Switch, TextField, Tooltip } from '@mui/material';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
    WEEKDAYS, SLOT_MINUTES_OPTIONS, MAX_INTERVALS_PER_DAY, companyHoursAsIntervals, toMinutes,
} from '../../../utils/resourceSchedule';
import styles from './ResourceFormPopup.module.css';

const LIMIT_FIELDS = [
    { key: 'maxPerDay', label: 'Por dia' },
    { key: 'maxPerMorning', label: 'Manhã', hint: 'até 12:00' },
    { key: 'maxPerAfternoon', label: 'Tarde', hint: 'a partir de 12:00' },
];

/** Erro da faixa: término antes do início ou sobreposta à anterior. */
const intervalError = (intervals, index) => {
    const [start, end] = intervals[index];
    if (toMinutes(end) <= toMinutes(start)) return 'Término antes do início';
    if (index > 0 && toMinutes(start) < toMinutes(intervals[index - 1][1])) return 'Sobrepõe a faixa anterior';
    return '';
};

/**
 * Horário de atendimento da agenda: dias e faixas próprios (desligado = segue
 * Preferências), intervalo dos horários no link público e máximo de atendimentos
 * por dia e por período. O valor é resource.Schedule; vazio vira null ao salvar.
 */
function ResourceScheduleBlock({ schedule, companyConfig, onChange }) {
    const current = schedule || {};
    const hours = current.hours || null;
    const update = (patch) => onChange({ ...current, ...patch });

    const toggleOwnHours = (checked) => {
        update({ hours: checked ? companyHoursAsIntervals(companyConfig) : null });
    };

    const setDayIntervals = (day, intervals) => {
        const next = { ...hours };
        if (intervals.length === 0) delete next[String(day)];
        else next[String(day)] = intervals;
        update({ hours: next });
    };

    const toggleDay = (day) => {
        const existing = hours[String(day)];
        if (existing && existing.length > 0) {
            setDayIntervals(day, []);
            return;
        }
        // Dia novo começa com a faixa do primeiro dia preenchido (ou 08:00–18:00)
        const firstFilled = WEEKDAYS.map((w) => hours[String(w.day)]).find((i) => i && i.length > 0);
        setDayIntervals(day, firstFilled ? firstFilled.map((pair) => [...pair]) : [['08:00', '18:00']]);
    };

    const changeInterval = (day, index, position, value) => {
        const intervals = hours[String(day)].map((pair) => [...pair]);
        intervals[index][position] = value;
        setDayIntervals(day, intervals);
    };

    const addInterval = (day) => {
        const intervals = hours[String(day)].map((pair) => [...pair]);
        const lastEnd = intervals[intervals.length - 1][1];
        const start = Math.min(toMinutes(lastEnd) + 60, 23 * 60);
        const pad = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
        intervals.push([pad(start), pad(Math.min(start + 4 * 60, 23 * 60 + 55))]);
        setDayIntervals(day, intervals);
    };

    const removeInterval = (day, index) => {
        setDayIntervals(day, hours[String(day)].filter((_, i) => i !== index));
    };

    return (
        <>
            <div className={styles.toggleCard}>
                <label className={styles.toggleRow}>
                    <span className={styles.blockIcon}><ScheduleOutlinedIcon fontSize="small" /></span>
                    <span className={styles.toggleText}>
                        <span className={styles.blockTitle}>Horário próprio</span>
                        <span className={styles.hint}>
                            {hours ? 'Dias e horários desta agenda.' : 'Desligado: segue os dias e horários de Preferências.'}
                        </span>
                    </span>
                    <Switch
                        checked={!!hours}
                        onChange={(e) => toggleOwnHours(e.target.checked)}
                        color="primary"
                        inputProps={{ 'aria-label': 'Usar horário próprio nesta agenda' }}
                    />
                </label>

                {hours && (
                    <div className={styles.toggleBody}>
                        {WEEKDAYS.map(({ day, label }) => {
                            const intervals = hours[String(day)] || [];
                            const works = intervals.length > 0;
                            return (
                                <div key={day} className={styles.dayRow}>
                                    <button
                                        type="button"
                                        className={`${styles.dayChip} ${works ? styles.dayChipOn : ''}`}
                                        onClick={() => toggleDay(day)}
                                        aria-pressed={works}
                                    >
                                        {label}
                                    </button>
                                    <div className={styles.dayIntervals}>
                                        {!works && <span className={styles.hint}>Não atende</span>}
                                        {intervals.map((pair, index) => {
                                            const error = intervalError(intervals, index);
                                            return (
                                                <div key={index} className={styles.intervalRow}>
                                                    <TextField
                                                        type="time"
                                                        size="small"
                                                        value={pair[0]}
                                                        onChange={(e) => changeInterval(day, index, 0, e.target.value)}
                                                        inputProps={{ step: 300, 'aria-label': `Início ${label}` }}
                                                        error={!!error}
                                                        className={`${styles.input} ${styles.timeInput}`}
                                                    />
                                                    <span className={styles.hint}>às</span>
                                                    <TextField
                                                        type="time"
                                                        size="small"
                                                        value={pair[1]}
                                                        onChange={(e) => changeInterval(day, index, 1, e.target.value)}
                                                        inputProps={{ step: 300, 'aria-label': `Término ${label}` }}
                                                        error={!!error}
                                                        className={`${styles.input} ${styles.timeInput}`}
                                                    />
                                                    {intervals.length > 1 && (
                                                        <Tooltip title="Remover faixa" arrow>
                                                            <IconButton size="small" onClick={() => removeInterval(day, index)} aria-label="Remover faixa">
                                                                <CloseRoundedIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {index === intervals.length - 1 && intervals.length < MAX_INTERVALS_PER_DAY && (
                                                        <Tooltip title="Adicionar faixa (ex.: depois do almoço)" arrow>
                                                            <IconButton size="small" onClick={() => addInterval(day)} aria-label="Adicionar faixa">
                                                                <AddRoundedIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {error && <span className={styles.intervalError}>{error}</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                        <span className={styles.hint}>
                            Para o almoço, use duas faixas. Na agenda interna, marcar fora deste horário só mostra um aviso.
                        </span>
                    </div>
                )}
            </div>

            <div className={styles.field}>
                <label className={styles.label} htmlFor="resource-slot-minutes">Horários no agendamento online a cada</label>
                <TextField
                    id="resource-slot-minutes"
                    select
                    value={current.slotMinutes || ''}
                    onChange={(e) => update({ slotMinutes: e.target.value === '' ? null : Number(e.target.value) })}
                    fullWidth
                    size="small"
                    className={styles.input}
                    SelectProps={{ displayEmpty: true }}
                >
                    <MenuItem value="">Igual a Preferências</MenuItem>
                    {SLOT_MINUTES_OPTIONS.map((minutes) => (
                        <MenuItem key={minutes} value={minutes}>{minutes} min</MenuItem>
                    ))}
                </TextField>
            </div>

            <div className={styles.field}>
                <span className={styles.label}>Máximo de atendimentos</span>
                <div className={styles.limitsRow}>
                    {LIMIT_FIELDS.map(({ key, label, hint }) => (
                        <div key={key} className={styles.limitField}>
                            <label className={styles.hint} htmlFor={`resource-limit-${key}`}>
                                {label}{hint ? ` (${hint})` : ''}
                            </label>
                            <TextField
                                id={`resource-limit-${key}`}
                                type="number"
                                size="small"
                                value={current[key] ?? ''}
                                onChange={(e) => update({ [key]: e.target.value === '' ? null : e.target.value })}
                                inputProps={{ min: 1, max: 200, 'aria-label': label }}
                                placeholder="Sem limite"
                                className={styles.input}
                            />
                        </div>
                    ))}
                </div>
                <span className={styles.hint}>
                    Vazio = sem limite. No link público o horário some ao chegar no limite; na agenda interna só avisa.
                </span>
            </div>
        </>
    );
}

export default ResourceScheduleBlock;
